import React, { useState, useMemo } from 'react';
import { GameState, TransferPlayer, Player, League, TeamInLeague } from '../../../domain/entities';
import { getPlayerValue } from '../../../domain/constants';
import { PlayerFace } from '../../shared/PlayerFace';
import { TeamBadge } from '../../shared/TeamBadge';
import { useToast } from '../../../contexts/ToastContext';
import { soundService } from '../../../services/SoundService';
import { isPlayerRecruitmentBlocked } from '../../../services/GameService';
import { View } from '../../../App';
import { PotentialBadge } from '../../shared/PotentialBadge';
import { FitnessBadge } from '../../shared/FitnessBadge';
import { BuyPlayerFilter } from './BuyPlayerFilter';
import { NegotiationModal } from '../../modals/NegotiationModal';
import { useTableFilters } from '../../../hooks/useTableFilters';
import { TableHeader } from '../../shared/TableHeader';
import { AdvancedFilterModal, FilterOption } from '../../shared/AdvancedFilterModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAmbulance } from '@fortawesome/free-solid-svg-icons';

type UIBuyPlayer = TransferPlayer & { isListed: boolean };

interface BuyPlayersProps {
    gameState: GameState,
    playerTeam: TeamInLeague,
    onNegotiatePurchase: (player: TransferPlayer, offerMultiplier: number) => Promise<{ success: boolean; message: string; acceptedOffer?: number; }>;
    onFinalizePurchase: (player: TransferPlayer, transferFee: number, wageMultiplier: number) => Promise<{ success: boolean; message: string; }>;
    pushView: (view: View) => void;
    filters?: any;
    mode?: 'listed' | 'scout'; // Which list to display ('listed' for transfer list, 'scout' for scout players)
}

export const BuyPlayers: React.FC<BuyPlayersProps> = ({
    gameState, playerTeam, onNegotiatePurchase, onFinalizePurchase, pushView, filters, mode = 'listed'
}) => {
    const { addToast } = useToast();
    const [negotiatingPlayer, setNegotiatingPlayer] = useState<UIBuyPlayer | null>(null);
    const [negotiationStep, setNegotiationStep] = useState<'initial' | 'player_terms' | 'complete'>('initial');
    const [selectedOfferMultiplier, setSelectedOfferMultiplier] = useState(1.0);
    const [selectedWageMultiplier, setSelectedWageMultiplier] = useState(1.0);
    const [acceptedOffer, setAcceptedOffer] = useState(0);
    const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const ITEMS_PER_PAGE = 50;

    // Calculate block status for the negotiating player
    const blockStatus = useMemo(() => {
        if (!negotiatingPlayer) return { blocked: false, weeksRemaining: 0 };
        return isPlayerRecruitmentBlocked(negotiatingPlayer, gameState.season, gameState.currentWeek, gameState.config);
    }, [negotiatingPlayer, gameState.season, gameState.currentWeek, gameState.config]);

    // New filtering system
    const {
        filters: tableFilters,
        updateFilter,
        clearFilters,
        isFilterModalOpen,
        setIsFilterModalOpen,
        hasActiveFilters
    } = useTableFilters('buy-players', {
        search: '',
        sortBy: 'skill_desc',
        positionFilter: 'ALL',
        leagueFilter: 'ALL',
        hideAmbitious: true,
        minSkill: 0,
        maxSkill: 99,
        maxAge: 40,
        minValue: 0,
        maxValue: 50000000,
        favFootFilter: 'ALL',
    });
    
    const allLeagueNames = gameState.config.LEAGUE_NAMES;

    if (gameState.transferBanned) {
        return (
            <div className="panel">
                <div className="panel-header">Transfer Market</div>
                <div className="panel-content" style={{ padding: '24px', textAlign: 'center' }}>
                    <p className="negative" style={{fontSize: '1.1em'}}>🚫 Your club is currently under a transfer ban. You cannot sign any new players.</p>
                </div>
            </div>
        );
    }

    const { playerTeamLeagueAverageSkill, myLeagueRank } = useMemo(() => {
        const league = gameState.leagues[gameState.playerLeagueName];
        if (!league) return { playerTeamLeagueAverageSkill: 50, myLeagueRank: 3 };
        
        const allPlayers = league.teams.flatMap(t => t.squad);
        const avgSkill = allPlayers.length > 0
            ? allPlayers.reduce((sum, p) => sum + p.skill, 0) / allPlayers.length
            : 50;
        
        const rank = allLeagueNames.indexOf(gameState.playerLeagueName);

        return { playerTeamLeagueAverageSkill: avgSkill, myLeagueRank: rank };
    }, [gameState.leagues, gameState.playerLeagueName, allLeagueNames]);
    
    const transferList = useMemo((): (TransferPlayer & { isListed: boolean })[] => {
        const playerTeamIds = new Set(playerTeam.squad.map(p => p.id));
        const listedPlayerIds = new Set(gameState.playersForSale);

        return Object.values(gameState.leagues).flatMap((league: League) => 
            league.teams.flatMap((t: TeamInLeague) => 
                t.squad
                .filter(player => !playerTeamIds.has(player.id))
                .map(player => ({
                    ...player,
                    askingPrice: getPlayerValue(player, league.name, gameState.gameType, gameState.config),
                    originalTeamName: t.name,
                    originalLeagueName: league.name,
                    isListed: listedPlayerIds.has(player.id),
                }))
            )
        );
    }, [gameState, playerTeam.squad]);

    const teamColorsMap = useMemo(() => {
        const map = new Map<string, string>();
        Object.values(gameState.leagues).forEach((league: League) =>
            league.teams.forEach((t: TeamInLeague) =>
                map.set(t.name, t.colors.primary)
            )
        );
        return map;
    }, [gameState.leagues]);

    const listedForSale = useMemo(() => transferList.filter(p => p.isListed), [transferList]);
    const scoutablePlayers = useMemo(() => transferList.filter(p => !p.isListed), [transferList]);

    const activeListSource = mode === 'listed' ? listedForSale : scoutablePlayers;

    const filteredAndSortedList = useMemo(() => {
        return [...activeListSource]
            .filter(p => p.name.toLowerCase().includes(tableFilters.search.toLowerCase()))
            .filter(p => tableFilters.positionFilter === 'ALL' || p.position === tableFilters.positionFilter)
            .filter(p => tableFilters.leagueFilter === 'ALL' || p.originalLeagueName === tableFilters.leagueFilter)
            .filter(p => p.skill >= tableFilters.minSkill && p.skill <= tableFilters.maxSkill)
            .filter(p => p.age <= tableFilters.maxAge)
            .filter(p => p.askingPrice >= tableFilters.minValue && p.askingPrice <= tableFilters.maxValue)
            .filter(p => tableFilters.favFootFilter === 'ALL' || p.favoredFoot === tableFilters.favFootFilter)
            .filter(p => {
                if (!tableFilters.hideAmbitious) return true;
                const skillDifference = p.skill - playerTeamLeagueAverageSkill;
                const playerLeagueRank = allLeagueNames.indexOf(p.originalLeagueName);
                const isAmbitious = skillDifference > 8 && playerLeagueRank <= myLeagueRank && p.age < 30;
                return !isAmbitious;
            })
            .sort((a, b) => {
                switch (tableFilters.sortBy) {
                    case 'potential_desc': return b.potential - a.potential;
                    case 'fitness_desc': return b.fitness - a.fitness;
                    case 'fitness_asc': return a.fitness - b.fitness;
                    case 'price_desc': return b.askingPrice - a.askingPrice;
                    case 'price_asc': return a.askingPrice - b.askingPrice;
                    case 'name_asc': return a.name.localeCompare(b.name);
                    case 'age_asc': return a.age - b.age;
                    case 'skill_desc':
                    default: return b.skill - a.skill;
                }
            });
    }, [activeListSource, tableFilters, playerTeamLeagueAverageSkill, myLeagueRank, allLeagueNames]);

    // Reset pagination when filters or tab changes
    React.useEffect(() => {
        setCurrentPage(1);
    }, [tableFilters, mode]);

    // Calculate paginated list
    const totalPages = Math.ceil(filteredAndSortedList.length / ITEMS_PER_PAGE);
    const paginatedList = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        const endIndex = startIndex + ITEMS_PER_PAGE;
        return filteredAndSortedList.slice(startIndex, endIndex);
    }, [filteredAndSortedList, currentPage]);

    const startNegotiation = (player: UIBuyPlayer) => {
        soundService.playClick();
        setNegotiatingPlayer(player);
        setNegotiationStep('initial');
        setSelectedOfferMultiplier(player.isListed ? 1.0 : 1.2);
        setMessage(null);
        setIsSubmitting(false);
    };

    const handleNegotiation = async () => {
        soundService.playClick();
        if (!negotiatingPlayer) return;
        setIsSubmitting(true);
        setMessage(null);

        const result = await onNegotiatePurchase(negotiatingPlayer, selectedOfferMultiplier);
        
        if (result.success) {
            soundService.playSuccess();
            setAcceptedOffer(result.acceptedOffer!);
            setNegotiationStep('player_terms');
            setSelectedWageMultiplier(1.0);
            setMessage({ type: 'success', text: result.message });
        } else {
            soundService.playError();
            setMessage({ type: 'error', text: result.message });
        }
        setIsSubmitting(false);
    };

    const handleFinalize = async () => {
        soundService.playClick();
        if (!negotiatingPlayer) return;
        setIsSubmitting(true);
        setMessage(null);

        const result = await onFinalizePurchase(negotiatingPlayer, acceptedOffer, selectedWageMultiplier);
        setMessage({ type: result.success ? 'success' : 'error', text: result.message });

        if (result.success) {
            soundService.playSuccess();
            setNegotiationStep('complete');
            // Show toast with navigation after a short delay to let modal close
            setTimeout(() => {
                addToast(`${negotiatingPlayer.name} signed successfully! Click to view squad.`, 'success', () => {
                    pushView({ type: 'team', title: 'Squad' });
                });
            }, 500);
        } else {
            soundService.playError();
        }
        setIsSubmitting(false);
    };
    
    const closeModal = () => {
        setNegotiatingPlayer(null);
        setMessage(null);
    };

    const handleViewPlayer = (player: Player) => {
        soundService.playClick();
        pushView({ type: 'playerDetail', title: `Player: ${player.name}`, playerId: player.id });
    };

    const handleTeamClick = (e: React.MouseEvent, teamName: string) => {
        e.stopPropagation();
        soundService.playClick();
        pushView({ type: 'teamDetail', title: `Team: ${teamName}`, teamName });
    };

    const handleLeagueClick = (e: React.MouseEvent, leagueName: string) => {
        e.stopPropagation();
        soundService.playClick();
        pushView({ type: 'competitions', title: 'Competitions', leagueName: leagueName, activeTab: 'league' });
    };

    const getTeamByName = (name: string) => {
        for (const league of Object.values(gameState.leagues)) {
            const team = league.teams.find(t => t.name === name);
            if (team) return team;
        }
        return undefined;
    };

    return (
        <>
            <div className="panel">
                <div className="panel-header">Transfer Market</div>
                {mode === 'scout' && (
                    <div style={{padding: '12px', backgroundColor: 'var(--primary-color)', borderBottom: 'var(--border)'}}>
                        <p style={{fontSize: '0.9em', opacity: 0.9}}>Players not on the transfer list are harder and more expensive to sign.</p>
                    </div>
                )}
                <TableHeader
                    searchValue={tableFilters.search}
                    onSearchChange={(value) => updateFilter('search', value)}
                    searchPlaceholder="Search player names..."
                    sortValue={tableFilters.sortBy}
                    onSortChange={(value) => updateFilter('sortBy', value)}
                    sortOptions={[
                        { label: 'Skill (High to Low)', value: 'skill_desc' },
                        { label: 'Skill (Low to High)', value: 'skill_asc' },
                        { label: 'Potential (High to Low)', value: 'potential_desc' },
                        { label: 'Age (Young to Old)', value: 'age_asc' },
                        { label: 'Price (High to Low)', value: 'price_desc' },
                        { label: 'Price (Low to High)', value: 'price_asc' },
                        { label: 'Fitness (High to Low)', value: 'fitness_desc' },
                        { label: 'Name (A to Z)', value: 'name_asc' },
                    ]}
                    onFilterClick={() => setIsFilterModalOpen(true)}
                    onClearFilters={clearFilters}
                    hasActiveFilters={hasActiveFilters}
                />
                <div className="table-wrapper">
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Pos</th>
                                <th>Cat</th>
                                <th>Foot</th>
                                <th>Age</th>
                                <th>Skl</th>
                                <th>Pot</th>
                                <th>Fit</th>
                                <th>Price</th>
                                <th>From</th>
                                <th>League</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedList.length === 0 && filteredAndSortedList.length === 0 && hasActiveFilters && (
                                <tr>
                                    <td colSpan={12} style={{ textAlign: 'center', padding: '32px 16px', opacity: 0.8 }}>
                                        <p style={{ fontSize: '1em', marginBottom: '8px' }}>No players match your filters.</p>
                                        <p style={{ fontSize: '0.9em', opacity: 0.7 }}>Try adjusting your search criteria or clearing some filters.</p>
                                    </td>
                                </tr>
                            )}
                            {paginatedList.map(p => (
                                <tr key={p.id} className={p.originalTeamName === gameState.playerTeamName ? 'highlight' : ''}>
                                    <td>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <PlayerFace player={p} teamColor={teamColorsMap.get(p.originalTeamName)} />
                                            {p.injury && <FontAwesomeIcon icon={faAmbulance} className="negative" title={`${p.injury.type} Injury (${p.injury.duration} wks)`} />}
                                            <button className="link-style" onClick={() => handleViewPlayer(p)}>{p.name}</button>
                                            {p.isListed && <span className="positive" style={{fontSize: '0.8em', fontWeight: 'bold'}}>Listed</span>}
                                        </div>
                                    </td>
                                    <td>{p.position}</td>
                                    <td title={p.favoredPositionCategory}>{p.favoredPositionCategory?.substring(0, 3)}</td>
                                    <td title={p.favoredFoot}>{p.favoredFoot?.substring(0, 3)}</td>
                                    <td>{p.age}</td>
                                    <td>{p.skill}</td>
                                    <td><PotentialBadge potential={p.potential} /></td>
                                    <td><FitnessBadge fitness={p.fitness} /></td>
                                    <td>£{p.askingPrice.toLocaleString()}</td>
                                    <td><button className="link-style" onClick={(e) => handleTeamClick(e, p.originalTeamName)}>{getTeamByName(p.originalTeamName)?.badge && <TeamBadge badge={getTeamByName(p.originalTeamName)?.badge} color={getTeamByName(p.originalTeamName)?.colors.primary} size="sm" />}{p.originalTeamName}</button></td>
                                    <td>
                                        <button className="link-style" onClick={(e) => handleLeagueClick(e, p.originalLeagueName)}>
                                            {p.originalLeagueName}
                                        </button>
                                    </td>
                                    <td>
                                        {(() => {
                                            const playerBlockStatus = isPlayerRecruitmentBlocked(p, gameState.season, gameState.currentWeek, gameState.config);
                                            return (
                                                <button
                                                    className="button small"
                                                    onClick={(e) => { e.stopPropagation(); startNegotiation(p as UIBuyPlayer); }}
                                                    disabled={playerBlockStatus.blocked}
                                                    title={playerBlockStatus.blocked ? `Blocked for ${playerBlockStatus.weeksRemaining} more weeks` : ''}
                                                    data-ai-target-buy-player-id={p.id}
                                                >
                                                    {playerBlockStatus.blocked ? `Blocked (${playerBlockStatus.weeksRemaining}w)` : 'Make Offer'}
                                                </button>
                                            );
                                        })()}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {filteredAndSortedList.length > ITEMS_PER_PAGE && (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', padding: '16px', backgroundColor: 'var(--primary-color)', borderTop: 'var(--border)' }}>
                        <button
                            className="button small"
                            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                            disabled={currentPage === 1}
                        >
                            ← Prev
                        </button>
                        <span style={{ fontSize: '0.9em', opacity: 0.8 }}>
                            Page {currentPage} of {totalPages} ({filteredAndSortedList.length} results)
                        </span>
                        <button
                            className="button small"
                            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                            disabled={currentPage === totalPages}
                        >
                            Next →
                        </button>
                    </div>
                )}
            </div>

            <AdvancedFilterModal
                isOpen={isFilterModalOpen}
                onClose={() => setIsFilterModalOpen(false)}
                filters={tableFilters}
                onFilterChange={updateFilter}
                onClearFilters={clearFilters}
                filterOptions={[
                    { label: 'Position', type: 'select', key: 'positionFilter', options: [
                        { label: 'All Positions', value: 'ALL' },
                        { label: 'Goalkeeper', value: 'GK' },
                        { label: 'Defender', value: 'DEF' },
                        { label: 'Midfielder', value: 'MID' },
                        { label: 'Forward', value: 'FWD' },
                    ]},
                    { label: 'Preferred Foot', type: 'select', key: 'favFootFilter', options: [
                        { label: 'All Feet', value: 'ALL' },
                        { label: 'Left', value: 'Left' },
                        { label: 'Right', value: 'Right' },
                    ]},
                    { label: 'League', type: 'select', key: 'leagueFilter', options: [
                        { label: 'All Leagues', value: 'ALL' },
                        ...allLeagueNames.map(name => ({ label: name, value: name })),
                    ]},
                    { label: 'Min Skill', type: 'range', key: 'minSkill', min: 0, max: 99, step: 1 },
                    { label: 'Max Skill', type: 'range', key: 'maxSkill', min: 0, max: 99, step: 1 },
                    { label: 'Max Age', type: 'range', key: 'maxAge', min: 16, max: 40, step: 1 },
                    { label: 'Min Value', type: 'range', key: 'minValue', min: 0, max: 50000000, step: 100000 },
                    { label: 'Max Value', type: 'range', key: 'maxValue', min: 0, max: 50000000, step: 100000 },
                    { label: 'Hide Ambitious', type: 'checkbox', key: 'hideAmbitious', placeholder: 'Hide players unlikely to join' },
                ]}
                title="Transfer Market Filters"
            />

            <NegotiationModal
                player={negotiatingPlayer}
                onClose={closeModal}
                onNegotiate={handleNegotiation}
                onFinalize={handleFinalize}
                step={negotiationStep}
                message={message}
                isSubmitting={isSubmitting}
                selectedOfferMultiplier={selectedOfferMultiplier}
                onOfferMultiplierChange={setSelectedOfferMultiplier}
                selectedWageMultiplier={selectedWageMultiplier}
                onWageMultiplierChange={setSelectedWageMultiplier}
                assistantManagerLevel={gameState.assistantManagerLevel}
                blockStatus={blockStatus}
            />
        </>
    );
};
